import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Crop, MoveUpRight, Pencil, RotateCcw, Square, Undo2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Editor rápido antes de mandar uma foto ao cliente: seta, rabisco, retângulo
// de destaque e recorte. Tudo em canvas, sem biblioteca: o resultado sai como
// um arquivo novo que substitui o anexo.

type Ferramenta = 'seta' | 'rabisco' | 'retangulo' | 'recortar';
type Ponto = { x: number; y: number };
interface Traco {
  tipo: 'seta' | 'rabisco' | 'retangulo';
  cor: string;
  largura: number;
  pontos: Ponto[];
}
// Histórico: cada passo é um traço ou um recorte (que guarda a imagem de antes).
type Passo =
  | { tipo: 'traco'; traco: Traco }
  | { tipo: 'recorte'; antes: HTMLCanvasElement; tracosAntes: Traco[] };

const CORES = ['#ef4444', '#facc15', '#22c55e', '#3b82f6', '#ffffff', '#111827'];

const FERRAMENTAS: { id: Ferramenta; rotulo: string; icone: React.ReactNode }[] = [
  { id: 'seta', rotulo: 'Seta', icone: <MoveUpRight size={17} /> },
  { id: 'rabisco', rotulo: 'Rabiscar', icone: <Pencil size={17} /> },
  { id: 'retangulo', rotulo: 'Destacar', icone: <Square size={17} /> },
  { id: 'recortar', rotulo: 'Recortar', icone: <Crop size={17} /> },
];

function desenharTraco(ctx: CanvasRenderingContext2D, t: Traco) {
  ctx.save();
  ctx.strokeStyle = t.cor;
  ctx.fillStyle = t.cor;
  ctx.lineWidth = t.largura;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const [a, b] = [t.pontos[0], t.pontos[t.pontos.length - 1]];
  if (!a || !b) return ctx.restore();
  if (t.tipo === 'rabisco') {
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    for (const p of t.pontos.slice(1)) ctx.lineTo(p.x, p.y);
    ctx.stroke();
  } else if (t.tipo === 'retangulo') {
    ctx.strokeRect(
      Math.min(a.x, b.x),
      Math.min(a.y, b.y),
      Math.abs(b.x - a.x),
      Math.abs(b.y - a.y)
    );
  } else {
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    const ponta = t.largura * 4.5;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x - Math.cos(ang) * ponta * 0.6, b.y - Math.sin(ang) * ponta * 0.6);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(
      b.x - ponta * Math.cos(ang - Math.PI / 7),
      b.y - ponta * Math.sin(ang - Math.PI / 7)
    );
    ctx.lineTo(
      b.x - ponta * Math.cos(ang + Math.PI / 7),
      b.y - ponta * Math.sin(ang + Math.PI / 7)
    );
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

function copiarCanvas(origem: HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = origem.width;
  c.height = origem.height;
  c.getContext('2d')!.drawImage(origem, 0, 0);
  return c;
}

export function EditorImagem({
  arquivo,
  onCancelar,
  onConcluir,
}: {
  arquivo: File;
  onCancelar: () => void;
  onConcluir: (editado: File) => void;
}) {
  const telaRef = useRef<HTMLCanvasElement>(null);
  const baseRef = useRef<HTMLCanvasElement | null>(null);
  const originalRef = useRef<HTMLCanvasElement | null>(null);
  const [pronto, setPronto] = useState(false);
  const [ferramenta, setFerramenta] = useState<Ferramenta>('seta');
  const [cor, setCor] = useState(CORES[0]);
  const [tracos, setTracos] = useState<Traco[]>([]);
  const [historico, setHistorico] = useState<Passo[]>([]);
  const [emAndamento, setEmAndamento] = useState<Traco | null>(null);
  const [selecao, setSelecao] = useState<{ a: Ponto; b: Ponto } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const arrastando = useRef(false);
  // Cópia síncrona do traço em andamento: num arraste rápido o "soltar" chega
  // antes do re-render e o estado ainda teria só o primeiro ponto.
  const andamentoRef = useRef<Traco | null>(null);
  const selecaoRef = useRef<{ a: Ponto; b: Ponto } | null>(null);
  const mudarAndamento = (t: Traco | null) => {
    andamentoRef.current = t;
    setEmAndamento(t);
  };
  const mudarSelecao = (sel: { a: Ponto; b: Ponto } | null) => {
    selecaoRef.current = sel;
    setSelecao(sel);
  };

  // Carrega a foto escolhida numa tela base, no tamanho real.
  useEffect(() => {
    const url = URL.createObjectURL(arquivo);
    const img = new Image();
    img.onload = () => {
      const base = document.createElement('canvas');
      base.width = img.naturalWidth;
      base.height = img.naturalHeight;
      base.getContext('2d')!.drawImage(img, 0, 0);
      baseRef.current = base;
      originalRef.current = copiarCanvas(base);
      setPronto(true);
      URL.revokeObjectURL(url);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  // Traço proporcional à foto: fino num print, visível numa foto de 12 MP.
  const largura = baseRef.current
    ? Math.max(4, Math.round(Math.max(baseRef.current.width, baseRef.current.height) / 220))
    : 6;

  const redesenhar = useCallback(() => {
    const tela = telaRef.current;
    const base = baseRef.current;
    if (!tela || !base) return;
    tela.width = base.width;
    tela.height = base.height;
    const ctx = tela.getContext('2d')!;
    ctx.drawImage(base, 0, 0);
    for (const t of tracos) desenharTraco(ctx, t);
    if (emAndamento) desenharTraco(ctx, emAndamento);
    if (selecao) {
      const x = Math.min(selecao.a.x, selecao.b.x);
      const y = Math.min(selecao.a.y, selecao.b.y);
      const w = Math.abs(selecao.b.x - selecao.a.x);
      const h = Math.abs(selecao.b.y - selecao.a.y);
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, tela.width, y);
      ctx.fillRect(0, y + h, tela.width, tela.height - y - h);
      ctx.fillRect(0, y, x, h);
      ctx.fillRect(x + w, y, tela.width - x - w, h);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = Math.max(2, largura / 2);
      ctx.setLineDash([largura * 2, largura]);
      ctx.strokeRect(x, y, w, h);
      ctx.restore();
    }
  }, [tracos, emAndamento, selecao, largura]);

  useEffect(() => {
    if (pronto) redesenhar();
  }, [pronto, redesenhar]);

  const pontoDoEvento = (e: React.PointerEvent<HTMLCanvasElement>): Ponto => {
    const tela = telaRef.current!;
    const r = tela.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * tela.width,
      y: ((e.clientY - r.top) / r.height) * tela.height,
    };
  };

  const aoApertar = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!pronto) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    arrastando.current = true;
    const p = pontoDoEvento(e);
    if (ferramenta === 'recortar') mudarSelecao({ a: p, b: p });
    else mudarAndamento({ tipo: ferramenta, cor, largura, pontos: [p] });
  };

  const aoMover = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!arrastando.current) return;
    const p = pontoDoEvento(e);
    if (ferramenta === 'recortar') {
      const sel = selecaoRef.current;
      if (sel) mudarSelecao({ ...sel, b: p });
    } else {
      const t = andamentoRef.current;
      if (t)
        mudarAndamento({
          ...t,
          pontos: t.tipo === 'rabisco' ? [...t.pontos, p] : [t.pontos[0], p],
        });
    }
  };

  const aoSoltar = () => {
    if (!arrastando.current) return;
    arrastando.current = false;
    const t = andamentoRef.current;
    if (t && t.pontos.length > 1) {
      setTracos((ts) => [...ts, t]);
      setHistorico((h) => [...h, { tipo: 'traco', traco: t }]);
    }
    mudarAndamento(null);
  };

  const aplicarRecorte = () => {
    const base = baseRef.current;
    const selecao = selecaoRef.current;
    if (!selecao || !base) return;
    const x = Math.round(Math.min(selecao.a.x, selecao.b.x));
    const y = Math.round(Math.min(selecao.a.y, selecao.b.y));
    const w = Math.round(Math.abs(selecao.b.x - selecao.a.x));
    const h = Math.round(Math.abs(selecao.b.y - selecao.a.y));
    if (w < 10 || h < 10) return mudarSelecao(null);
    // Os traços entram na imagem antes do recorte (senão ficariam fora do lugar).
    const comTracos = copiarCanvas(base);
    const ctx = comTracos.getContext('2d')!;
    for (const t of tracos) desenharTraco(ctx, t);
    const nova = document.createElement('canvas');
    nova.width = w;
    nova.height = h;
    nova.getContext('2d')!.drawImage(comTracos, x, y, w, h, 0, 0, w, h);
    setHistorico((hist) => [...hist, { tipo: 'recorte', antes: base, tracosAntes: tracos }]);
    baseRef.current = nova;
    setTracos([]);
    mudarSelecao(null);
    setFerramenta('seta');
  };

  const desfazer = useCallback(() => {
    setHistorico((hist) => {
      const ultimo = hist[hist.length - 1];
      if (!ultimo) return hist;
      if (ultimo.tipo === 'traco') setTracos((ts) => ts.slice(0, -1));
      else {
        baseRef.current = ultimo.antes;
        setTracos(ultimo.tracosAntes);
      }
      return hist.slice(0, -1);
    });
  }, []);

  const recomecar = () => {
    if (originalRef.current) baseRef.current = copiarCanvas(originalRef.current);
    setTracos([]);
    setHistorico([]);
    mudarSelecao(null);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selecao) mudarSelecao(null);
        else onCancelar();
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        desfazer();
      } else if (e.key === 'Enter' && selecao) aplicarRecorte();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // aplicarRecorte muda a cada render; a seleção é o que importa aqui.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecao, desfazer, onCancelar]);

  const concluir = () => {
    const tela = telaRef.current;
    if (!tela) return;
    setSalvando(true);
    mudarSelecao(null);
    // Redesenha sem a moldura do recorte antes de exportar.
    const base = baseRef.current!;
    const saida = copiarCanvas(base);
    const ctx = saida.getContext('2d')!;
    for (const t of tracos) desenharTraco(ctx, t);
    const png = arquivo.type === 'image/png';
    saida.toBlob(
      (blob) => {
        setSalvando(false);
        if (!blob) return;
        const nome = arquivo.name.replace(/\.[a-z0-9]+$/i, '') + (png ? '.png' : '.jpg');
        onConcluir(new File([blob], nome, { type: png ? 'image/png' : 'image/jpeg' }));
      },
      png ? 'image/png' : 'image/jpeg',
      0.9
    );
  };

  const nada = historico.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Editar imagem"
      className="fixed inset-0 z-[150] flex flex-col bg-black"
    >
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <button
          type="button"
          onClick={onCancelar}
          aria-label="Cancelar edição"
          className="flex size-9 items-center justify-center rounded-full text-white/80 hover:bg-white/10 hover:text-white"
        >
          <X size={20} />
        </button>
        <p className="mr-auto text-sm font-semibold text-white">Editar imagem</p>
        <button
          type="button"
          onClick={desfazer}
          disabled={nada}
          title="Desfazer (Ctrl+Z)"
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white/80 hover:bg-white/10 disabled:opacity-30"
        >
          <Undo2 size={15} /> Desfazer
        </button>
        <button
          type="button"
          onClick={recomecar}
          disabled={nada}
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white/80 hover:bg-white/10 disabled:opacity-30"
        >
          <RotateCcw size={15} /> Recomeçar
        </button>
        <button
          type="button"
          onClick={concluir}
          disabled={!pronto || salvando}
          className="ml-1 flex items-center gap-1.5 rounded-lg bg-tertiary px-4 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          <Check size={15} /> Usar imagem
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-4">
        <canvas
          ref={telaRef}
          onPointerDown={aoApertar}
          onPointerMove={aoMover}
          onPointerUp={aoSoltar}
          onPointerCancel={aoSoltar}
          className={cn(
            'max-h-full max-w-full touch-none rounded-md object-contain shadow-2xl',
            ferramenta === 'recortar' ? 'cursor-crosshair' : 'cursor-crosshair'
          )}
        />
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 border-t border-white/10 px-4 py-3">
        <div className="flex items-center gap-1 rounded-xl bg-white/5 p-1">
          {FERRAMENTAS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => {
                setFerramenta(f.id);
                if (f.id !== 'recortar') mudarSelecao(null);
              }}
              aria-pressed={ferramenta === f.id}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors',
                ferramenta === f.id ? 'bg-white text-black' : 'text-white/80 hover:bg-white/10'
              )}
            >
              {f.icone}
              {f.rotulo}
            </button>
          ))}
        </div>
        {ferramenta !== 'recortar' ? (
          <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Cor">
            {CORES.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={cor === c}
                aria-label={`Cor ${c}`}
                onClick={() => setCor(c)}
                style={{ backgroundColor: c }}
                className={cn(
                  'size-7 rounded-full border-2 transition-transform',
                  cor === c ? 'scale-110 border-white' : 'border-white/20'
                )}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-white/70">
            {selecao ? (
              <>
                <button
                  type="button"
                  onClick={aplicarRecorte}
                  className="rounded-lg bg-white px-3 py-2 font-semibold text-black hover:opacity-90"
                >
                  Aplicar recorte
                </button>
                <button
                  type="button"
                  onClick={() => mudarSelecao(null)}
                  className="rounded-lg px-3 py-2 font-semibold text-white/80 hover:bg-white/10"
                >
                  Limpar
                </button>
              </>
            ) : (
              'Arraste sobre a imagem para escolher a área.'
            )}
          </div>
        )}
      </div>
    </div>
  );
}
